import {useCallback, useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

/**
 * Debounced query parameter - user types, URL updates after delay
 */
function useDebouncedQueryParam(
  key: string,
  defaultValue: string = '',
  delay: number = 300,
): [
  string, // current value (instant, for input display)
  (value: string) => void, // setter (instant local state, debounced URL)
  {
    /** The debounced value (same as URL value) */
    debounced: string;
    /** Force immediate URL update */
    flush: () => void;
  },
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localValue, setLocalValue] = useState(
    () => searchParams.get(key) ?? defaultValue,
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const latestValueRef = useRef(localValue);

  // Keep ref in sync
  latestValueRef.current = localValue;

  // Sync URL with debounce
  const syncToUrl = useCallback(() => {
    const value = latestValueRef.current;
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        if (value && value !== defaultValue) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
        return newParams;
      },
      {replace: true},
    );
  }, [key, defaultValue, setSearchParams]);

  const debouncedSync = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(syncToUrl, delay);
  }, [syncToUrl, delay]);

  // Set value: update local state immediately, URL with debounce
  const setValue = useCallback(
    (value: string) => {
      setLocalValue(value);
      debouncedSync();
    },
    [debouncedSync],
  );

  // Flush any pending updates
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    syncToUrl();
  }, [syncToUrl]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Sync from URL (e.g., browser back/forward)
  const urlValue = searchParams.get(key) ?? defaultValue;

  return [
    localValue, // instant value for the input
    setValue, // update with debounce
    {
      debounced: urlValue, // value from URL (debounced)
      flush, // force immediate update
    },
  ];
}

export default useDebouncedQueryParam;
